import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Divider,
  Chip,
  Autocomplete,
  FormHelperText,
  FormControl,
} from "@mui/material";
import {
  Edit,
  AddCircleOutline,
  Save,
  UploadFile,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  addVisa} from "../../../Redux/action/AddVisaInformationAction";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import { toast } from "react-toastify";
import { useCountries } from "../../../Hooks/useCountries";

const PRIMARY_COLOR = "#286a94";

// const COUNTRIES = [
//   "India",
//   "United States",
//   "United Kingdom",
//   "Canada",
//   "Australia",
//   "Singapore",
//   "UAE",
//   "Germany",
//   "France",
//   "Japan",
//   "China",
//   "Netherlands",
//   "Switzerland",
//   "Sweden",
//   "Qatar",
//   "Saudi Arabia",
//   "Spain",
//   "Italy",
//   "New Zealand",
//   "Malaysia",
//   "South Africa",
//   "Ireland",
//   "Thailand",
// ];

const VISA_TYPES = [
  "Visa-Free",
  "Tourist Visa",
  "Business Visa",
  "Work Visa",
  "Employment Visa",
  "Skilled Worker Visa",
  "Student Visa",
  "Exchange Visa",
  "Dependent/Family Visa",
  "Investor Visa",
  "Permanent Residence (PR)",
  "Visitor Visa",
  "Transit Visa",
];

const VisaInformation = ({ candidateId }) => {
  const sectionRef = useRef(null);
  const dispatch = useDispatch();
  const { data: countries } = useCountries();
  const [visaList, setVisaList] = useState([]);
  const [editingVisaId, setEditingVisaId] = useState(null);
  const [errors, setErrors] = useState({});

  const [previewFile, setPreviewFile] = useState(null);

  const [form, setForm] = useState({
    visaType: "",
    visaNumber: "",
    nationality: "",
    placeOfBirth: "",
    countryOfResidence: "",
    visaCountry: "",
    visaIssuer: "",
    visaIssueDate: "",
    visaValidityDate: "",
    visaExpiryDate: "",
    visaImage: null,
  });

  const loadData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_PATH}/ebench/get_visa_information/${candidateId}`
      );
      setVisaList(res.data?.data?.visaInformation || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadData();
  }, [candidateId]);

  const resetForm = () => {
    setEditingVisaId(null);
    setPreviewFile(null);
    setForm({
      visaType: "",
      visaNumber: "",
      nationality: "",
      placeOfBirth: "",
      countryOfResidence: "",
      visaCountry: "",
      visaIssuer: "",
      visaIssueDate: "",
      visaValidityDate: "",
      visaExpiryDate: "",
      visaImage: null,
    });
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, visaImage: file });
    setPreviewFile(URL.createObjectURL(file));
  };

  const getStatus = (expiry) => {
    if (!expiry) return "Pending";
    return new Date(expiry) < new Date() ? "Expired" : "Active";
  };

  /** ---------- ADD VISA ---------- */
  const handleAdd = async () => {
    const validationErrors = validateVisaForm(form);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const formData = new FormData();

    const payload = {
      candidateId,
      visaInformation: [
        {
          ...form,
          visaImage: undefined,
        },
      ],
      travelHistory: [],
    };

    formData.append("visaInformation", JSON.stringify(payload));
    if (form.visaImage) formData.append("file", form.visaImage);

    await dispatch(addVisa(formData));
    resetForm();
    loadData();
  };

  const validateVisaForm = (form) => {
    const errors = {};
    console.log(form);
    Object.keys(form).forEach((field) => {
      if (!form[field] || String(form[field]).trim() === "") {
        const label = field
          .replace(/([A-Z])/g, " $1")
          .trim()
          .replace(/^./, (c) => c.toUpperCase());

        errors[field] = `${label} is required`;
      }
    });

    return errors;
  };

  /** ---------- UPDATE VISA ---------- */
  const handleUpdate = async () => {
    try {
      const formData = new FormData();

      const payload = {
        visaType: form.visaType,
        visaNumber: form.visaNumber,
        nationality: form.nationality,
        placeOfBirth: form.placeOfBirth,
        countryOfResidence: form.countryOfResidence,
        visaCountry: form.visaCountry,
        visaIssuer: form.visaIssuer,
        visaIssueDate: form.visaIssueDate,
        visaValidityDate: form.visaValidityDate,
        visaExpiryDate: form.visaExpiryDate,
      };

      formData.append("visaInformation", JSON.stringify(payload));

      if (form.visaImage instanceof File) {
        formData.append("file", form.visaImage);
      }

      const resp = await axios.put(
        `${process.env.REACT_APP_API_PATH}/ebench/update_visa/${editingVisaId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success(
        resp?.data?.data?.message || "Visa updated successfully 🎉"
      );

      resetForm();
      loadData();
    } catch (error) {
      console.error("Update Visa Error:", error);

      toast.error(
        error?.response?.data?.status?.message ||
          error?.message ||
          "Failed to update visa ❌"
      );
    }
  };

  const handleDelete = async (id) => {
    await axios.delete(
      `${process.env.REACT_APP_API_PATH}/ebench/delete_visa/${id}`
    );
    loadData();
  };

  const openEdit = (visa) => {
    console.log(visa);
    setEditingVisaId(visa.visaId);
    setForm({
      ...visa,
      visaImage: null,
    });

    if (visa.visaUrl) setPreviewFile(visa.visaUrl);

    setTimeout(
      () => sectionRef.current?.scrollIntoView({ behavior: "smooth" }),
      150
    );
  };

  const isFormValid =
    form.visaType && form.visaNumber && form.visaCountry && form.nationality;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY_COLOR }}>
          Visa Information
        </Typography>

        <Button
          variant="contained"
          size="small"
          disabled={!isFormValid}
          onClick={editingVisaId ? handleUpdate : handleAdd}
          sx={{
            textTransform: "none",
            backgroundColor: PRIMARY_COLOR,
            borderRadius: "8px",
            px: 1.5,
            display: "flex",
            alignItems: "center",
            "&:hover": { backgroundColor: PRIMARY_COLOR },
          }}
          startIcon={editingVisaId ? <Save /> : <AddCircleOutline />}
        >
          {editingVisaId ? "Save" : "Add"}
        </Button>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Form */}
      <Paper
        ref={sectionRef}
        sx={{ p: 3, mb: 3, borderLeft: `4px solid ${PRIMARY_COLOR}` }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={countries?.data || []}
              getOptionLabel={(option) => option.country || ""}
              value={
                countries?.data?.find((c) => c.country === form.visaCountry) ||
                null
              }
              onChange={(_, selectedOption) =>
                setForm({
                  ...form,
                  visaCountry: selectedOption?.country,
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visa Country"
                  fullWidth
                  error={Boolean(errors.visaCountry)}
                  helperText={errors.visaCountry}
                />
              )}
            />
          </Grid>

          {/* Visa Type */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={VISA_TYPES || []}
              value={form.visaType || null}
              onChange={(_, newValue) =>
                setForm({
                  ...form,
                  visaType: newValue || "",
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visa Type"
                  fullWidth
                  error={Boolean(errors.visaType)}
                  helperText={errors.visaType}
                />
              )}
              sx={{
                "& .MuiOutlinedInput-root": {
                  ...(errors.visaType && {
                    "& fieldset": {
                      borderColor: "red !important",
                    },
                  }),
                },
              }}
            />
          </Grid>

          {/* Country */}
          {/* <Grid item xs={12} md={6}>
            <Autocomplete
              options={COUNTRIES}
              value={form.visaCountry || ""}
              onChange={(_, value) => setForm({ ...form, visaCountry: value })}
              renderInput={(params) => (
                <TextField {...params} label="Visa Country" fullWidth />
              )}
            />
          </Grid> */}

          {/* Fields */}
          {[
            "visaNumber",
            "nationality",
            "placeOfBirth",
            "countryOfResidence",
            "visaIssuer",
          ].map((field) => (
            <Grid key={field} item xs={12} md={6}>
              <TextField
                fullWidth
                label={field
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
                name={field}
                value={form[field] || ""}
                onChange={handleChange}
                error={Boolean(errors[field])}
                helperText={errors[field]}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...(errors[field] && {
                      "& fieldset": {
                        borderColor: "red !important",
                      },
                    }),
                  },
                }}
              />
            </Grid>
          ))}

          {[
            { name: "visaIssueDate", label: "Issue Date" },
            { name: "visaValidityDate", label: "Validity Date" },
            { name: "visaExpiryDate", label: "Expiry Date" },
          ].map((f) => (
            <Grid key={f.name} item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label={f.label}
                name={f.name}
                value={form[f.name] || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors[f.name])}
                helperText={errors[f.name]}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...(errors[f.name] && {
                      "& fieldset": {
                        borderColor: "red !important",
                      },
                    }),
                  },
                }}
              />
            </Grid>
          ))}

          {/* Upload */}
          <Grid item xs={12}>
            <FormControl fullWidth error={Boolean(errors.visaImage)}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                sx={{
                  borderColor: Boolean(errors.visaImage)
                    ? "red"
                    : PRIMARY_COLOR,
                  color: Boolean(errors.visaImage) ? "red" : PRIMARY_COLOR,
                  "&:hover": {
                    borderColor: Boolean(errors.visaImage)
                      ? "darkred"
                      : PRIMARY_COLOR,
                    color: Boolean(errors.visaImage)
                      ? "darkred"
                      : PRIMARY_COLOR,
                  },
                }}
              >
                Upload Visa File
                <input
                  hidden
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleFileUpload}
                />
              </Button>

              {(previewFile || form.visaUrl) && (
                <Typography sx={{ mt: 1 }} fontSize={14}>
                  📁 File Attached
                </Typography>
              )}

              {errors.visaFile && (
                <FormHelperText sx={{ mt: 1 }}>
                  {errors.visaImage}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        {editingVisaId && (
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            color="error"
            onClick={resetForm}
          >
            Cancel Edit
          </Button>
        )}
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Table */}
      <Paper sx={{ p: 2, overflow: "hidden" }}>
        {/* Scroll Wrapper */}
        <Box
          sx={{
            overflowX: "auto",
            width: "100%",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 1200, // ensures layout stays clean
              "& th": {
                fontWeight: 600,
                background: "#f4f9fc",
                whiteSpace: "nowrap",
              },
              "& td": { whiteSpace: "nowrap" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Nationality</TableCell>
                <TableCell>Birth Place</TableCell>
                <TableCell>Residence</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Issuer</TableCell>
                <TableCell>Issued</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Proof</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visaList?.length ? (
                visaList.map((visa) => (
                  <TableRow key={visa.visaId}>
                    <TableCell>{visa.visaType}</TableCell>
                    <TableCell>{visa.visaNumber}</TableCell>
                    <TableCell>{visa.nationality}</TableCell>
                    <TableCell>{visa.placeOfBirth}</TableCell>
                    <TableCell>{visa.countryOfResidence}</TableCell>
                    <TableCell>{visa.visaCountry}</TableCell>
                    <TableCell>{visa.visaIssuer}</TableCell>
                    <TableCell>{visa.visaIssueDate}</TableCell>
                    <TableCell>{visa.visaValidityDate}</TableCell>
                    <TableCell>{visa.visaExpiryDate}</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={getStatus(visa.visaExpiryDate)}
                        color={
                          getStatus(visa.visaExpiryDate) === "Active"
                            ? "success"
                            : getStatus(visa.visaExpiryDate) === "Expired"
                            ? "error"
                            : "warning"
                        }
                      />
                    </TableCell>

                    <TableCell>
                      {visa.visaImage ? (
                        <Tooltip title="View File">
                          <IconButton
                            component="a"
                            href={visa.visaImage}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: PRIMARY_COLOR }}
                          >
                            {visa.visaImage.endsWith(".pdf") ? (
                              <PictureAsPdfIcon />
                            ) : (
                              <InsertPhotoIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{ color: PRIMARY_COLOR }}
                          onClick={() => openEdit(visa)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>

                      {/* <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(visa.visaId)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip> */}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={13}>
                    No visa records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
};

export default VisaInformation;
